// frontend/src/components/dashboard/TransportationManager.jsx
import React, { useState, useEffect } from 'react';
import { PlusIcon, TruckIcon, CalendarIcon, MapPinIcon, CurrencyRupeeIcon } from '@heroicons/react/24/outline';
import groupDashboardService from '../../services/groupDashboardService';
import Button from '../common/Button';


const TransportationManager = ({ groupId, membership, user }) => {
  const [transportations, setTransportations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [newTransportation, setNewTransportation] = useState({
    type: 'car',
    departure: { location: '', dateTime: '' },
    arrival: { location: '', dateTime: '' },
    provider: '',
    cost: '',
    notes: ''
  });
  
  useEffect(() => {
    loadTransportations();
  }, [groupId]);
  
  const loadTransportations = async () => {
    try {
      setLoading(true);
      const transportationsData = await groupDashboardService.getTransportations(groupId);
      setTransportations(transportationsData);
    } catch (error) {
      console.error('Failed to load transportations:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddTransportation = async (e) => {
    e.preventDefault();
    try {
      await groupDashboardService.addTransportation(groupId, newTransportation);
      
      setNewTransportation({
        type: 'car',
        departure: { location: '', dateTime: '' },
        arrival: { location: '', dateTime: '' },
        provider: '',
        cost: '',
        notes: ''
      });
      setShowAddForm(false);
      loadTransportations();
    } catch (error) {
      console.error('Failed to add transportation:', error);
    }
  };
  
  const transportationTypes = [
    { id: 'flight', name: 'Flight', icon: '✈️' },
    { id: 'train', name: 'Train', icon: '🚆' },
    { id: 'bus', name: 'Bus', icon: '🚌' },
    { id: 'car', name: 'Car', icon: '🚗' },
    { id: 'bike', name: 'Bike', icon: '🚲' },
    { id: 'walking', name: 'Walking', icon: '🚶' },
    { id: 'boat', name: 'Boat', icon: '🚢' },
    { id: 'other', name: 'Other', icon: '🚀' }
  ];
  
  const statusColors = {
    planned: 'bg-yellow-100 text-yellow-800',
    booked: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  
  if (loading) {
    return <div className="text-center py-8">Loading transportations...</div>;
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Transportation</h2>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-4 py-2 rounded-lg"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Transportation
        </Button>
      </div>
      
      {/* Add Transportation Form */}
      {showAddForm && (
        <div className="mb-6 p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-blue-100">
          <h3 className="font-bold text-gray-800 mb-4 text-lg">Add Transportation</h3>
          
          <form onSubmit={handleAddTransportation} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transportation Type
                </label>
                <select
                  value={newTransportation.type}
                  onChange={(e) => setNewTransportation({ ...newTransportation, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {transportationTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.icon} {type.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provider/Company
                </label>
                <input
                  type="text"
                  value={newTransportation.provider}
                  onChange={(e) => setNewTransportation({ ...newTransportation, provider: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Indian Railways"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departure Location
                </label>
                <input
                  type="text"
                  value={newTransportation.departure.location}
                  onChange={(e) => setNewTransportation({ 
                    ...newTransportation, 
                    departure: { ...newTransportation.departure, location: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Mumbai Central"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arrival Location
                </label>
                <input
                  type="text"
                  value={newTransportation.arrival.location}
                  onChange={(e) => setNewTransportation({ 
                    ...newTransportation, 
                    arrival: { ...newTransportation.arrival, location: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Goa Station"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departure Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={newTransportation.departure.dateTime}
                  onChange={(e) => setNewTransportation({ 
                    ...newTransportation, 
                    departure: { ...newTransportation.departure, dateTime: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost (₹)
                </label>
                <input
                  type="number"
                  value={newTransportation.cost}
                  onChange={(e) => setNewTransportation({ ...newTransportation, cost: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  step="0.01"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={newTransportation.notes}
                onChange={(e) => setNewTransportation({ ...newTransportation, notes: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                placeholder="Any special notes or instructions..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-6 py-2 rounded-lg"
              >
                Add Transportation
              </Button>
            </div>
          </form>
        </div>
      )}
      
      {/* Transportations List */}
      <div className="space-y-4">
        {transportations.length === 0 ? (
          <div className="text-center py-12">
            <TruckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Transportation Added</h3>
            <p className="text-gray-600 mb-6">Add your transportation details to share with the group!</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-6 py-3 rounded-lg"
            >
              Add First Transportation
            </Button>
          </div>
        ) : (
          transportations.map((transport) => {
            const transportType = transportationTypes.find(t => t.id === transport.type);
            
            return (
              <div key={transport._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">{transportType?.icon || '🚀'}</span>
                      <span className="text-sm text-gray-600 capitalize">{transport.type.replace('_', ' ')}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{transport.provider}</h3>
                    <p className="text-gray-600 mt-1">
                      {transport.departure?.location} → {transport.arrival?.location}
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-end">
                    {transport.cost > 0 && (
                      <div className="text-right mb-2">
                        <div className="text-sm text-gray-500">Cost</div>
                        <div className="text-2xl font-bold text-blue-600">₹{transport.cost}</div>
                      </div>
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[transport.status]}`}>
                      {transport.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {transport.departure?.dateTime && (
                    <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                      <CalendarIcon className="h-5 w-5 text-blue-500 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Departure</p>
                        <p className="font-medium">
                          {new Date(transport.departure.dateTime).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {transport.arrival?.dateTime && (
                    <div className="flex items-center p-3 bg-green-50 rounded-lg">
                      <CalendarIcon className="h-5 w-5 text-green-500 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Arrival</p>
                        <p className="font-medium">
                          {new Date(transport.arrival.dateTime).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {transport.notes && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{transport.notes}</p>
                  </div>
                )}
                
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-2">
                      {transport.createdBy?.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">{transport.createdBy?.name}</p>
                      <p className="text-xs text-gray-500">
                        Added {new Date(transport.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    Capacity: {transport.bookedSeats || 0}/{transport.capacity || 1}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TransportationManager;